import { Component } from "@theme/component";

/**
 * Button row custom element
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class ButtonRow extends Component {
  #current = 0;

  async connectedCallback() {
    super.connectedCallback();

    // init disable/enable button state
    this.updateNext();
    this.updatePrev();
  }

  get current() {
    return this.#current;
  }

  get #slides() {
    return Array.from(
      this.querySelectorAll(".button-row__slider .button-block"),
    );
  }

  set current(current) {
    this.#current = current;
  }

  /**
   * @param {Event} event
   */
  next(event) {
    event.preventDefault();
    if (this.#current === this.#slides.length - 1) return;
    this.current += 1;
    this.scrollToCurrent();
  }

  /**
   * @param {Event} event
   */
  previous(event) {
    event.preventDefault();
    if (this.#current === 0) return;
    this.current -= 1;
    this.scrollToCurrent();
  }

  scrollToCurrent() {
    const current = /** @type {HTMLElement|null} */ (
      this.#slides?.[this.#current]
    );
    if (!current) return;
    current.focus({ focusVisible: true });
    current.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });

    this.updateNext();
    this.updatePrev();
  }

  updateNext() {
    const isDisabled = this.refs.next?.getAttribute("disabled") === "true";
    const shouldBeDisabled = this.#current === this.#slides.length - 1;

    if (isDisabled && !shouldBeDisabled) {
      this.refs.next?.removeAttribute("disabled");
    } else if (!isDisabled && shouldBeDisabled) {
      this.refs.next?.setAttribute("disabled", "true");
    }
  }

  updatePrev() {
    const isDisabled = this.refs.previous?.getAttribute("disabled") === "true";
    const shouldBeDisabled = this.#current === 0;

    if (isDisabled && !shouldBeDisabled) {
      this.refs.previous?.removeAttribute("disabled");
    } else if (!isDisabled && shouldBeDisabled) {
      this.refs.previous?.setAttribute("disabled", "true");
    }
  }
}

if (!customElements.get("button-row-component")) {
  customElements.define("button-row-component", ButtonRow);
}
